import React, { useState, useEffect, useRef } from "react";
import "../../Styles/AdabiddaNidhiFOrm.css";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { aadabiddaNidhiAPI, fileAPI, BASE_URL } from "../../api/index";

const MANDAL_NAMES = [
  "Kandukur",
  "Lingasamudram",
  "Gudluru",
  "Ulavapadu",
  "Voletivaripalem",
];

// Matches the backend status values
const STATUS_OPTIONS = ["pending", "approved", "rejected", "disbursed"];
const STATUS_LABEL = {
  pending: "Pending",
  approved: "Approved",
  rejected: "Rejected",
  disbursed: "Disbursed",
};

const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
const PHOTO_TYPES = ["image/jpeg", "image/png", "image/webp"];
const DOC_TYPES = ["application/pdf", "image/jpeg", "image/png", "image/webp"];

// /files/upload only accepts fixed subfolders; "beneficiaries" is one of them
const UPLOAD_SUBFOLDER = "beneficiaries";

const initialForm = {
  name: "",
  relation: "",
  age: "",
  voterId: "",
  aadhaar: "",
  mobile: "",
  bankAccount: "",
  ifsc: "",
  amount: "1500",
  village: "",
  mandal: "",
  date: "",
  status: "",
  remarks: "",
};

const extractErrorMessage = (err) => {
  if (!err.response) {
    return `Network error — could not reach the server (${err.message}).`;
  }
  const status = err.response.status;
  const detail = err.response.data?.detail ?? err.response.data?.message;

  if (status === 404) {
    return typeof detail === "string"
      ? `[404] ${detail} — double check the spelling of the Village/Mandal name.`
      : `[404] Village or Mandal not recognized. Check spelling and try again.`;
  }
  if (typeof detail === "string") return `[${status}] ${detail}`;
  if (Array.isArray(detail)) {
    const parts = detail.slice(0, 3).map((d) => {
      if (d.row !== undefined) return `Row ${d.row}: ${d.reason}`;
      if (d.loc) return `${d.loc.join(".")}: ${d.msg}`;
      return d.msg || d.reason || JSON.stringify(d);
    });
    return `[${status}] ${parts.join(" | ")}`;
  }
  return `[${status}] Request failed — see console for full response body`;
};

// "/uploads/beneficiaries/x.png" -> full backend URL
const toFullUrl = (p) =>
  /^https?:\/\//i.test(p) ? p : `${BASE_URL}${p.startsWith("/") ? "" : "/"}${p}`;

// Uploads one file and returns its full URL
const uploadFile = async (file) => {
  const res = await fileAPI.upload(file, UPLOAD_SUBFOLDER);
  const d = res.data;
  const path = typeof d === "string" ? d : d?.url || d?.file_url || d?.path || d?.file_path;
  if (!path) throw new Error("Upload did not return a file URL");
  return toFullUrl(path);
};

const AdminAdaBiddaNidhiForm = () => {
  const navigate = useNavigate();

  const [formData, setFormData] = useState(initialForm);
  const [photo, setPhoto] = useState(null); // { file, preview }
  const [documentFile, setDocumentFile] = useState(null); // File
  const [submitting, setSubmitting] = useState(false);
  const [stage, setStage] = useState(""); // "", "uploading", "saving"

  const photoInputRef = useRef(null);
  const docInputRef = useRef(null);

  // free the photo preview when leaving the page
  const photoRef = useRef(photo);
  photoRef.current = photo;
  useEffect(() => {
    return () => {
      if (photoRef.current) URL.revokeObjectURL(photoRef.current.preview);
    };
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  /* ---------- files ---------- */
  const handlePhotoChange = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;

    if (!PHOTO_TYPES.includes(file.type)) {
      toast.error("Photo must be a JPG, PNG or WEBP image");
      return;
    }
    if (file.size > MAX_FILE_SIZE) {
      toast.error("Photo must be smaller than 5MB");
      return;
    }
    if (photo) URL.revokeObjectURL(photo.preview);
    setPhoto({ file, preview: URL.createObjectURL(file) });
  };

  const removePhoto = () => {
    if (photo) URL.revokeObjectURL(photo.preview);
    setPhoto(null);
  };

  const handleDocumentChange = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;

    if (!DOC_TYPES.includes(file.type)) {
      toast.error("Document must be a PDF or an image (JPG, PNG, WEBP)");
      return;
    }
    if (file.size > MAX_FILE_SIZE) {
      toast.error("Document must be smaller than 5MB");
      return;
    }
    setDocumentFile(file);
  };

  const handleReset = () => {
    if (photo) URL.revokeObjectURL(photo.preview);
    setFormData(initialForm);
    setPhoto(null);
    setDocumentFile(null);
  };

  /* ---------- submit ---------- */
  const handleSubmit = async (e) => {
    e.preventDefault();

    const trimmedVillage = formData.village.trim();

    if (!formData.name.trim()) {
      toast.error("Beneficiary name is required");
      return;
    }
    if (!trimmedVillage) {
      toast.error("Village is required");
      return;
    }
    if (!formData.mandal || formData.mandal === "Select Mandal") {
      toast.error("Please select a Mandal");
      return;
    }
    if (!formData.status || formData.status === "Select Status") {
      toast.error("Please select a Status");
      return;
    }
    if (formData.age !== "" && (Number(formData.age) < 18 || Number(formData.age) > 59)) {
      toast.error("Age must be between 18 and 59");
      return;
    }
    if (formData.aadhaar && !/^\d{12}$/.test(formData.aadhaar)) {
      toast.error("Aadhaar Number must be exactly 12 digits");
      return;
    }
    if (formData.mobile && !/^\d{10}$/.test(formData.mobile)) {
      toast.error("Mobile Number must be exactly 10 digits");
      return;
    }

    setSubmitting(true);
    try {
      // 1) upload the files first (if any) and get their URLs
      let photoUrl;
      let documentUrl;

      if (photo || documentFile) {
        setStage("uploading");
        if (photo) photoUrl = await uploadFile(photo.file);
        if (documentFile) documentUrl = await uploadFile(documentFile);
      }

      // 2) create the beneficiary
      setStage("saving");
      const payload = {
        beneficiary_name: formData.name.trim(),
        relation_name: formData.relation.trim() || undefined,
        epic_no: formData.voterId.trim().toUpperCase() || undefined,
        age: formData.age !== "" ? Number(formData.age) : undefined,
        aadhaar_number: formData.aadhaar.trim() || undefined,
        mobile_number: formData.mobile.trim() || undefined,
        bank_account_number: formData.bankAccount.trim() || undefined,
        ifsc_code: formData.ifsc.trim().toUpperCase() || undefined,
        amount: formData.amount !== "" ? Number(formData.amount) : undefined,
        mandal_name: formData.mandal,
        village_name: trimmedVillage,
        application_date: formData.date || undefined,
        status: formData.status,
        photo_url: photoUrl,
        document_url: documentUrl,
        remarks: formData.remarks.trim() || undefined,
      };

      try {
        await aadabiddaNidhiAPI.create(payload);
      } catch (err) {
        console.error("Aadabidda Nidhi create payload sent:", payload);
        throw err;
      }

      toast.success("Aadabidda Nidhi beneficiary added successfully");
      handleReset();
      navigate(-1); // back to the table
    } catch (err) {
      toast.error(
        err.response ? extractErrorMessage(err) : err.message || "Something went wrong"
      );
    } finally {
      setSubmitting(false);
      setStage("");
    }
  };

  const buttonText = !submitting
    ? "Submit"
    : stage === "uploading"
    ? "Uploading files..."
    : "Submitting...";

  return (
    <div className="adbddanidhi-form-container">
      <div className="adbddanidhi-form-card">
        <div className="adbddanidhi-form-header">
          <h2>Add New Aadabidda Nidhi Beneficiary</h2>
          <p>Financial assistance of ₹1,500/month for eligible women aged 18–59</p>
          <button
            type="button"
            className="adbddanidhi-back-btn"
            onClick={() => navigate(-1)}
          >
            Back
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="adbddanidhi-form-grid">
            <div className="adbddanidhi-form-group">
              <label>Beneficiary Name *</label>
              <input
                type="text"
                name="name"
                placeholder="Enter Beneficiary Name"
                value={formData.name}
                onChange={handleChange}
              />
            </div>

            <div className="adbddanidhi-form-group">
              <label>Relation</label>
              <input
                type="text"
                name="relation"
                placeholder="Ex: D/O, W/O"
                value={formData.relation}
                onChange={handleChange}
              />
            </div>

            <div className="adbddanidhi-form-group">
              <label>Voter ID</label>
              <input
                type="text"
                name="voterId"
                placeholder="Enter Voter Id"
                value={formData.voterId}
                onChange={handleChange}
              />
            </div>

            <div className="adbddanidhi-form-group">
              <label>Age</label>
              <input
                type="number"
                name="age"
                placeholder="Enter Age (18–59)"
                min="18"
                max="59"
                value={formData.age}
                onChange={handleChange}
              />
            </div>

            <div className="adbddanidhi-form-group">
              <label>Aadhaar Number</label>
              <input
                type="text"
                name="aadhaar"
                placeholder="Enter 12-digit Aadhaar Number"
                maxLength="12"
                value={formData.aadhaar}
                onChange={handleChange}
              />
            </div>

            <div className="adbddanidhi-form-group">
              <label>Mobile Number</label>
              <input
                type="tel"
                name="mobile"
                placeholder="Enter Mobile Number"
                maxLength="10"
                value={formData.mobile}
                onChange={handleChange}
              />
            </div>

            <div className="adbddanidhi-form-group">
              <label>Bank Account Number</label>
              <input
                type="text"
                name="bankAccount"
                placeholder="Enter Bank Account Number"
                value={formData.bankAccount}
                onChange={handleChange}
              />
            </div>

            <div className="adbddanidhi-form-group">
              <label>IFSC Code</label>
              <input
                type="text"
                name="ifsc"
                placeholder="Enter IFSC Code"
                value={formData.ifsc}
                onChange={handleChange}
              />
            </div>

            <div className="adbddanidhi-form-group">
              <label>Monthly Amount (₹)</label>
              <input type="number" name="amount" value={formData.amount} readOnly />
            </div>

            <div className="adbddanidhi-form-group">
              <label>Village *</label>
              <input
                type="text"
                name="village"
                placeholder="Enter Village"
                value={formData.village}
                onChange={handleChange}
              />
              <small
                style={{
                  color: "#9ca3af",
                  fontSize: "12px",
                  marginTop: "4px",
                  display: "block",
                }}
              >
                Must exactly match an existing village name under the selected Mandal.
              </small>
            </div>

            <div className="adbddanidhi-form-group">
              <label>Mandal *</label>
              <select name="mandal" value={formData.mandal} onChange={handleChange}>
                <option>Select Mandal</option>
                {MANDAL_NAMES.map((m) => (
                  <option key={m}>{m}</option>
                ))}
              </select>
            </div>

            <div className="adbddanidhi-form-group">
              <label>Date</label>
              <input
                type="date"
                name="date"
                value={formData.date}
                onChange={handleChange}
              />
            </div>

            <div className="adbddanidhi-form-group">
              <label>Status *</label>
              <select name="status" value={formData.status} onChange={handleChange}>
                <option>Select Status</option>
                {STATUS_OPTIONS.map((s) => (
                  <option key={s} value={s}>{STATUS_LABEL[s]}</option>
                ))}
              </select>
            </div>

            <div className="adbddanidhi-form-group">
              <label>Upload Photo</label>
              <input
                ref={photoInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                onChange={handlePhotoChange}
              />
              {photo && (
                <div style={{ position: "relative", width: 90, marginTop: 8 }}>
                  <img
                    src={photo.preview}
                    alt="Selected"
                    style={{
                      width: 90,
                      height: 90,
                      objectFit: "cover",
                      borderRadius: 8,
                      border: "1px solid #e2e8f0",
                    }}
                  />
                  <button
                    type="button"
                    onClick={removePhoto}
                    aria-label="Remove photo"
                    style={{
                      position: "absolute",
                      top: -6,
                      right: -6,
                      width: 20,
                      height: 20,
                      borderRadius: "50%",
                      background: "#dc2626",
                      color: "#fff",
                      border: "none",
                      fontSize: 12,
                      cursor: "pointer",
                    }}
                  >
                    ×
                  </button>
                </div>
              )}
            </div>

            <div className="adbddanidhi-form-group">
              <label>Upload Supporting Document</label>
              <input
                ref={docInputRef}
                type="file"
                accept=".pdf,image/*"
                onChange={handleDocumentChange}
              />
              {documentFile && (
                <small style={{ color: "#475569", marginTop: 6, display: "block" }}>
                  Selected: {documentFile.name}{" "}
                  <button
                    type="button"
                    onClick={() => setDocumentFile(null)}
                    style={{
                      border: "none",
                      background: "transparent",
                      color: "#dc2626",
                      cursor: "pointer",
                    }}
                  >
                    Remove
                  </button>
                </small>
              )}
            </div>
          </div>

          <div className="adbddanidhi-form-group adbddanidhi-full-width">
            <label>Remarks</label>
            <textarea
              rows="4"
              name="remarks"
              placeholder="Enter Remarks"
              value={formData.remarks}
              onChange={handleChange}
            ></textarea>
          </div>

          <div className="adbddanidhi-btn-group">
            <button
              type="submit"
              className="adbddanidhi-submit-btn"
              disabled={submitting}
            >
              {buttonText}
            </button>

            <button
              type="button"
              className="adbddanidhi-reset-btn"
              onClick={handleReset}
              disabled={submitting}
            >
              Reset
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AdminAdaBiddaNidhiForm;